import { useState, useEffect } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData, postData, updateData, deleteData, activateData } from '../../../apiService';
import LoadingOverlay from '../../common/LoadingOverlay';
import { useSessionValidation } from '../../../utils/sessionValidation';
import {
  Add, Edit, Visibility, Delete, Refresh, CheckCircle, Warning, Search as SearchIcon
} from '@mui/icons-material';
import {
  Button, IconButton, Tooltip, Chip, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Typography, Box,
  TextField, InputAdornment, Fade, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Paper, CircularProgress
} from '@mui/material';
import JournalEntryModal from './JournalEntryModal';
import Pagination from '../../common/Pagination';
import './JournalEntryIndex.css';


const JournalEntryIndex = () => {
  // Session validation
  useSessionValidation();

  const { companyId, branchId, token, userId, permissions } = useAuth();

  const hasCreatePermission = permissions?.includes('Create Master');
  const hasEditPermission = permissions?.includes('Edit Master');
  const hasDeletePermission = permissions?.includes('Delete Master');

  // Modal states
  const [openModal, setOpenModal] = useState(false);
  const [modalAction, setModalAction] = useState('');

  // Data states
  const [journalEntries, setJournalEntries] = useState([]);
  const [masters, setMasters] = useState([]);
  const [gstRates, setGstRates] = useState([]);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [mounted, setMounted] = useState(false);

  const formatDateToYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDefaultFromDate = () => {
    const today = new Date();
    return formatDateToYYYYMMDD(new Date(today.getFullYear(), today.getMonth(), 1));
  };
  const getCurrentDate = () => formatDateToYYYYMMDD(new Date());

  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(getCurrentDate());

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Form states
  const [formData, setFormData] = useState({});
  const [journalEntryDetails, setJournalEntryDetails] = useState([]);

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [modalLoading, setModalLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Delete/Activate confirmation modals
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemNo, setDeleteItemNo] = useState('');
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [activateItemId, setActivateItemId] = useState(null);
  const [activateItemNo, setActivateItemNo] = useState('');

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (companyId && token && fromDate && toDate) {
      fetchAllData();
    }
  }, [companyId, token, fromDate, toDate]);

  // Fetch functions
  const fetchJournalEntries = async () => {
    try {
      const activeResponse = await fetchData('JournalEntryApi', `/All/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      const activeData = (activeResponse.response_data || activeResponse || []).map(item => ({ ...item, IsActive: item.IsActive ?? true }));

      let inactiveData = [];
      try {
        const inactiveResponse = await fetchData('JournalEntryApi', `/InActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
        inactiveData = (inactiveResponse.response_data || inactiveResponse || []).map(item => ({ ...item, IsActive: false }));
      } catch (inactiveError) {
        console.error('Error fetching inactive journal entries:', inactiveError);
      }

      setJournalEntries([...activeData, ...inactiveData]);
    } catch (error) {
      console.error('Error fetching journal entries:', error);
      setJournalEntries([]);
    }
  };

  const fetchMasters = async () => {
    try {
      // Fetch all types of heads to populate the masters list
      const [mastersRes, vendorsRes, customersRes, banksRes, accountHeadsRes] = await Promise.allSettled([
        fetchData('JournalEntryApi', `/Master/${companyId}`, token),
        fetchData('VendorApi', `/${companyId}`, token),
        fetchData('CustomerApi', `/${companyId}`, token),
        fetchData('BankApi', `/${companyId}`, token),
        fetchData('AccountHeadApi', `/${companyId}`, token)
      ]);

      // Combine all masters data
      const mastersList = mastersRes.status === 'fulfilled' ? (mastersRes.value.response_data || mastersRes.value || []) : [];
      const vendorsList = vendorsRes.status === 'fulfilled' ? (vendorsRes.value.response_data || vendorsRes.value || []) : [];
      const customersList = customersRes.status === 'fulfilled' ? (customersRes.value.response_data || customersRes.value || []) : [];
      const banksList = banksRes.status === 'fulfilled' ? (banksRes.value.response_data || banksRes.value || []) : [];
      const accountHeadsList = accountHeadsRes.status === 'fulfilled' ? (accountHeadsRes.value.response_data || accountHeadsRes.value || []) : [];

      // Merge all lists into one masters array
      const allMasters = [
        ...mastersList,
        ...vendorsList,
        ...customersList,
        ...banksList,
        ...accountHeadsList
      ];

      setMasters(allMasters);
    } catch (error) {
      console.error('Error fetching masters:', error);
      setMasters([]);
    }
  };

  const fetchGstRates = async () => {
    try {
      const response = await fetchData('JournalEntryApi', `/GetGSTList/${companyId}`, token);
      setGstRates(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching GST rates:', error);
      // Fallback to default GST rates if API fails
      setGstRates([
        { Id: 1, Rate: 0, Name: '0%', IsActive: true },
        { Id: 2, Rate: 5, Name: '5%', IsActive: true },
        { Id: 3, Rate: 12, Name: '12%', IsActive: true },
        { Id: 4, Rate: 18, Name: '18%', IsActive: true },
        { Id: 5, Rate: 28, Name: '28%', IsActive: true }
      ]);
    }
  };

  const fetchMaxBillNo = async () => {
    try {
      const response = await fetchData('JournalEntryApi', `/MaxBillno/${branchId || 1}/${companyId}`, token);
      const data = response.response_data || response || {};
      const billNumber = data.BillNumber || data.billNumber || 1;
      const billPrefix = data.BillPrefix || data.billPrefix || '';
      const finYear = data.FinYear || data.finYear || '';
      return { billNumber, billPrefix, finYear };
    } catch (error) {
      console.error('Error fetching max bill number:', error);
      return { billNumber: 1, billPrefix: '', finYear: '' };
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    setLoadingMessage('Fetching journal entries...');
    try {
      await Promise.allSettled([
        fetchJournalEntries(),
        fetchMasters(),
        fetchGstRates()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle modal actions
  const handleOpenModal = async (action, item = null) => {
    setModalAction(action);

    if (action === 'create') {
      const maxBillData = await fetchMaxBillNo();
      const billNumber = maxBillData.billPrefix && maxBillData.finYear ?
        `${maxBillData.billPrefix}/${maxBillData.billNumber}/${maxBillData.finYear}` :
        maxBillData.billNumber.toString();

      setFormData({
        Id: 0,
        CompanyId: companyId,
        BranchId: branchId,
        BillNumber: billNumber,
        BillDate: new Date().toISOString().split('T')[0],
        IsType: '',
        MasterId: '',
        MasterType: 4, // Default to AccountHead (4)
        CustomerId: null,
        VendorId: null,
        AccountHeadId: null,
        BankId: null,
        Amount: 0,
        IsGST: false,
        IsIGST: false,
        GSTHeadId: '',
        GSTRate: 0,
        CGSTAmount: 0,
        SGSTAmount: 0,
        IGSTAmount: 0,
        TotalChildAmount: 0,
        Remarks: '',
        IsActive: true
      });
      setJournalEntryDetails([]);
    } else if (action === 'edit' || action === 'view') {
      setModalLoading(true);
      try {
        // Fetch complete journal entry data from API
        const response = await fetchData('JournalEntryApi', `/JournalEntryData/${item.Id}`, token);
        const journalEntryData = response.response_data || response;
        const journalData = journalEntryData.JournalEntry;

        setFormData({
          ...journalData,
          BillDate: journalData.BillDate ? journalData.BillDate.split('T')[0] : new Date().toISOString().split('T')[0],
          IsType: journalData.IsType === true ? 'Credit' : 'Debit',
          MasterId: journalData.MasterId || '',
          MasterType: journalData.MasterType || 4 // Ensure MasterType is set, default to 4 (AccountHead)
        });

        // Load all journal entry details without filtering
        const details = journalEntryData.JournalEntryDetails || [];
        setJournalEntryDetails(details);

      } catch (error) {
        console.error('Error fetching journal entry details:', error);
        setError('Failed to fetch journal entry details.');
        // Fallback to using the item data from the list
        setFormData({
          ...item,
          BillDate: item.BillDate ? item.BillDate.split('T')[0] : new Date().toISOString().split('T')[0],
          IsType: item.IsType === true ? 'Credit' : 'Debit',
          MasterId: item.MasterId || '',
          MasterType: item.MasterType || 4 // Ensure MasterType is set, default to 4 (AccountHead)
        });
        setJournalEntryDetails(item.JournalEntryDetails || []);
      }
      setModalLoading(false);
    }

    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setFormData({});
    setJournalEntryDetails([]);
  };

  // Handle save
  const handleSave = async () => {
    setModalLoading(true);
    try {
      const dataToSend = {
        JournalEntry: {
          Id: modalAction === 'edit' ? formData.Id : 0,
          CompanyId: companyId,
          BranchId: branchId,
          BillDate: formData.BillDate,
          BillNumber: parseInt(formData.BillNumber) || 0,
          FinYear: formData.FinYear || new Date().getFullYear().toString(),
          IsType: formData.IsType === 'Credit' ? true : false,
          MasterId: parseInt(formData.MasterId) || 0,
          MasterType: formData.MasterType || 4, // Include MasterType (default to 4 for AccountHead)
          CustomerId: formData.CustomerId ? parseInt(formData.CustomerId) : null,
          VendorId: formData.VendorId ? parseInt(formData.VendorId) : null,
          AccountHeadId: formData.AccountHeadId ? parseInt(formData.AccountHeadId) : null,
          BankId: formData.BankId ? parseInt(formData.BankId) : null,
          Amount: parseFloat(formData.Amount) || 0,
          Remarks: formData.Remarks || null,
          IsActive: true,
          CreatedBy: modalAction === 'create' ? userId : formData.CreatedBy,
          UpdatedBy: modalAction === 'edit' ? userId : undefined,
          CreatedDate: modalAction === 'create' ? new Date().toISOString() : formData.CreatedDate,
          UpdatedDate: modalAction === 'edit' ? new Date().toISOString() : undefined
        }
      };

      // Add journal entry details (child entries)
      if (journalEntryDetails.length > 0) {
        dataToSend.JournalEntryDetails = journalEntryDetails.map(detail => ({
          Id: detail.Id || 0,
          CompanyId: companyId,
          BranchId: branchId,
          JournalEntryId: modalAction === 'edit' ? formData.Id : 0,
          MasterId: parseInt(detail.MasterId),
          MasterType: detail.MasterType || 4, // Include MasterType for child entries (default to 4 for AccountHead)
          CustomerId: detail.CustomerId ? parseInt(detail.CustomerId) : null,
          VendorId: detail.VendorId ? parseInt(detail.VendorId) : null,
          AccountHeadId: detail.AccountHeadId ? parseInt(detail.AccountHeadId) : null,
          BankId: detail.BankId ? parseInt(detail.BankId) : null,
          Amount: parseFloat(detail.Amount) || 0,
          Remarks: detail.Remarks || null,
          IsActive: true,
          CreatedBy: modalAction === 'create' ? userId : detail.CreatedBy,
          UpdatedBy: modalAction === 'edit' ? userId : undefined,
          CreatedDate: modalAction === 'create' ? new Date().toISOString() : detail.CreatedDate,
          UpdatedDate: modalAction === 'edit' ? new Date().toISOString() : undefined
        }));
      }

      console.log('Saving Journal Entry data:', dataToSend);

      if (modalAction === 'create') {
        await postData('JournalEntryApi', dataToSend, token);
        setSuccess('Journal Entry created successfully!');
      } else {
        await updateData('JournalEntryApi', formData.Id, dataToSend, token);
        setSuccess('Journal Entry updated successfully!');
      }

      handleCloseModal();
      await fetchJournalEntries();
    } catch (error) {
      console.error('Error saving journal entry:', error);
      setError('Failed to save journal entry.');
    }
    setModalLoading(false);
  };

  // Handle delete
  const handleDelete = async () => {
    try {
      await deleteData('JournalEntryApi', deleteItemId, token);
      setSuccess('Journal Entry deleted successfully!');
      setDeleteModalOpen(false);
      await fetchJournalEntries();
    } catch (error) {
      console.error('Error deleting journal entry:', error);
      setError('Failed to delete journal entry.');
    }
  };

  // Handle activate
  const handleActivate = async () => {
    try {
      await activateData('JournalEntryApi', activateItemId, token);
      setSuccess('Journal Entry activated successfully!');
      setActivateModalOpen(false);
      await fetchJournalEntries();
    } catch (error) {
      console.error('Error activating journal entry:', error);
      setError('Failed to activate journal entry.');
    }
  };

  // Wrapper functions for new UI
  const handleDeleteConfirm = async () => {
    await handleDelete();
  };

  const handleActivateConfirm = async () => {
    await handleActivate();
  };

  const handleSaveJournalEntry = async () => {
    await fetchJournalEntries();
    setOpenModal(false);
  };

  // Filter and search logic
  const filteredData = journalEntries.filter(item => {
    const matchesSearch = !searchTerm ||
      item.BillNumber?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.Remarks?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'active' && item.IsActive) ||
      (filterStatus === 'inactive' && !item.IsActive);

    return matchesSearch && matchesStatus;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedData = filteredData.slice(startIndex, startIndex + rowsPerPage);

  const formatCurrency = (amount) => {
    return `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (!mounted) return null;

  return (
    <Fade in={mounted} timeout={800}>
      <div style={{
        minWidth: '100%',
        background: '#f5f7fa',
        padding: '10px',
        position: 'relative'
      }}>
        {/* Loading Overlay */}
        {loading && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              zIndex: 9999,
              backdropFilter: 'blur(4px)'
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress
                size={60}
                thickness={4}
                sx={{
                  color: '#667eea',
                  mb: 2
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  color: '#667eea',
                  fontWeight: 600,
                  fontSize: '1rem'
                }}
              >
                Loading Journal Entries...
              </Typography>
            </Box>
          </Box>
        )}

        <style>{`
          @keyframes slideIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .action-button {
            text-transform: none;
            font-weight: 600;
            letter-spacing: 0.5px;
          }
        `}</style>

        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          padding: '12px',
          position: 'relative',
          color: 'black',
          fontFamily: 'Times New Romans',
        }}>
          {/* Header Section */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px',
            paddingBottom: '8px',
            borderBottom: '1px solid #f0f0f0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div>
                <h4 style={{
                  fontSize: '17px',
                  fontWeight: '700',
                  margin: 0,
                  letterSpacing: '-0.3px',
                  fontFamily: 'Times New Romans',
                  textTransform: 'uppercase'
                }}>
                  <span style={{ color: 'black', fontFamily: 'Times New Romans', }}>Journal Entry</span>
                </h4>
              </div>
            </div>
          </div>

          {/* Date Filters, Search and Create Button Row */}
          <div style={{
            marginBottom: '12px',
            overflowX: 'auto',
            overflowY: 'hidden',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'thin'
          }}>
            <div style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'flex-end',
              minWidth: 'fit-content',
              paddingBottom: '4px'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '120px' : '140px' }}>
                <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>From Date</label>
                <TextField
                  type="date"
                  size="small"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  sx={{
                    width: isMobile ? '120px' : '140px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '11px',
                      height: '32px',
                      '&:hover fieldset': { borderColor: '#667eea' },
                      '&.Mui-focused fieldset': { borderColor: '#667eea' },
                    },
                  }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '120px' : '140px' }}>
                <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>To Date</label>
                <TextField
                  type="date"
                  size="small"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  sx={{
                    width: isMobile ? '120px' : '140px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '11px',
                      height: '32px',
                      '&:hover fieldset': { borderColor: '#667eea' },
                      '&.Mui-focused fieldset': { borderColor: '#667eea' },
                    },
                  }}
                />
              </div>

              {/* Search */}
              {isMobile ? (
                searchExpanded ? (
                  <TextField
                    size="small"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    autoFocus
                    onBlur={() => {
                      if (!searchTerm) {
                        setSearchExpanded(false);
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon style={{ color: '#667eea', fontSize: '18px' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      minWidth: '250px',
                      width: '350px',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        backgroundColor: '#f8f9fa',
                        fontSize: '12px',
                        height: '32px',
                        '&:hover fieldset': {
                          borderColor: '#667eea',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#667eea',
                        },
                      },
                    }}
                  />
                ) : (
                  <Tooltip title="Search" arrow>
                    <Button
                      onClick={() => setSearchExpanded(true)}
                      style={{
                        minWidth: '32px',
                        width: '32px',
                        height: '32px',
                        padding: '0',
                        borderRadius: '6px',
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #e2e8f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <SearchIcon style={{ color: '#667eea', fontSize: '18px' }} />
                    </Button>
                  </Tooltip>
                )
              ) : (
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ color: '#667eea', fontSize: '18px' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    flex: 1,
                    minWidth: '250px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '12px',
                      height: '32px',
                      '&:hover fieldset': {
                        borderColor: '#667eea',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                  }}
                />
              )}

              {/* <Tooltip title="Refresh data" arrow>
                <Button
                  variant="contained"
                  onClick={fetchAllData}
                  disabled={loading}
                  className="action-button"
                  style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    padding: isMobile ? '0' : '4px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '700',
                    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.4)',
                    border: 'none',
                    height: '32px',
                    textTransform: 'none',
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    whiteSpace: 'nowrap',
                    minWidth: isMobile ? '32px' : 'auto',
                    width: 'auto'
                  }}
                >
                  <Refresh style={{ fontSize: '18px' }} />
                  {!isMobile && <span style={{ fontSize: '12px' }}>Refresh</span>}
                </Button>
              </Tooltip> */}

              {hasCreatePermission && (
                <Tooltip title="Create a new journal entry" arrow>
                  <Button
                    variant="contained"
                    onClick={() => handleOpenModal('create')}
                    className="action-button"
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      padding: isMobile ? '0' : '4px 12px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '700',
                      boxShadow: '0 2px 8px rgba(102, 126, 234, 0.4)',
                      border: 'none',
                      height: '32px',
                      textTransform: 'none',
                      letterSpacing: '0.5px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                      whiteSpace: 'nowrap',
                      minWidth: isMobile ? '32px' : '100px',
                      width: 'auto',
                      marginLeft: 'auto'
                    }}
                  >
                    <Add style={{ fontSize: '18px' }} />
                    {!isMobile && <span style={{ fontSize: '12px' }}>Create</span>}
                  </Button>
                </Tooltip>
              )}
            </div>
          </div>

          {/* Table */}
          <div style={{ marginBottom: '10px' }}>
            <TableContainer component={Paper} style={{
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
              borderRadius: '10px',
              overflow: 'hidden'
            }}>
              <Table sx={{ minWidth: 650 }} aria-label="journal entry table">
                <TableHead>
                  <TableRow style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    height: '28px'
                  }}>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: '4px 8px',
                      textAlign: 'left',
                      height: '28px'
                    }}>
                      Voucher No
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: '4px 8px',
                      textAlign: 'left',
                      height: '28px'
                    }}>
                      Date
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: '4px 8px',
                      textAlign: 'left',
                      height: '28px'
                    }}>
                      Debit Amount
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: '4px 8px',
                      textAlign: 'left',
                      height: '28px'
                    }}>
                      Credit Amount
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: '4px 8px',
                      textAlign: 'left',
                      height: '28px'
                    }}>
                      Status
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: '4px 8px',
                      textAlign: 'right',
                      height: '28px'
                    }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                        <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.75rem' }}>
                          No journal entries found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedData.map((item) => (
                      <TableRow
                        key={item.Id}
                        sx={{
                          '&:nth-of-type(even)': { backgroundColor: '#fafbfc' },
                          '&:hover': {
                            backgroundColor: '#f1f5f9',
                            transform: 'scale(1.001)',
                            transition: 'all 0.2s ease-in-out'
                          },
                          borderBottom: '1px solid #f1f5f9'
                        }}
                      >
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e40af', fontSize: '0.75rem' }}>
                            {item.BillNumber}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem' }}>
                            FY: {item.FinYear}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                            {formatDate(item.BillDate)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#dc2626' }}>
                            {formatCurrency(item.DebitAmount)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#059669' }}>
                            {formatCurrency(item.CreditAmount)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Chip
                            label={item.IsActive ? 'Active' : 'Inactive'}
                            color={item.IsActive ? 'success' : 'default'}
                            size="small"
                            sx={{ fontSize: '10px', height: '20px' }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 1, textAlign: 'right' }}>
                          <Box sx={{ display: 'flex', gap: 0.3, justifyContent: 'flex-end' }}>
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenModal('view', item)}
                                sx={{
                                  color: '#3b82f6',
                                  backgroundColor: '#eff6ff',
                                  width: 28,
                                  height: 28,
                                  '&:hover': {
                                    backgroundColor: '#dbeafe',
                                    transform: 'scale(1.1)'
                                  }
                                }}
                              >
                                <Visibility sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>

                            {hasEditPermission && item.IsActive && (
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenModal('edit', item)}
                                  sx={{
                                    color: '#f59e0b',
                                    backgroundColor: '#fffbeb',
                                    width: 28,
                                    height: 28,
                                    '&:hover': {
                                      backgroundColor: '#fef3c7',
                                      transform: 'scale(1.1)'
                                    }
                                  }}
                                >
                                  <Edit sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            )}

                            {hasDeletePermission && (
                              <Tooltip title={item.IsActive ? 'Delete' : 'Activate'}>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    if (item.IsActive) {
                                      setDeleteItemId(item.Id);
                                      setDeleteItemNo(item.BillNumber);
                                      setDeleteModalOpen(true);
                                    } else {
                                      setActivateItemId(item.Id);
                                      setActivateItemNo(item.BillNumber);
                                      setActivateModalOpen(true);
                                    }
                                  }}
                                  sx={{
                                    color: item.IsActive ? '#ef4444' : '#10b981',
                                    backgroundColor: item.IsActive ? '#fef2f2' : '#d1fae5',
                                    width: 28,
                                    height: 28,
                                    '&:hover': {
                                      backgroundColor: item.IsActive ? '#fecaca' : '#a7f3d0',
                                      transform: 'scale(1.1)'
                                    }
                                  }}
                                >
                                  {item.IsActive ? <Delete sx={{ fontSize: 14 }} /> : <CheckCircle sx={{ fontSize: 14 }} />}
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filteredData.length}
            itemsPerPage={rowsPerPage}
            onPageChange={(page) => setCurrentPage(page)}
            onItemsPerPageChange={(newSize) => {
              setRowsPerPage(newSize);
              setCurrentPage(1);
            }}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </div>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Warning />
              Confirm Deletion
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <DialogContentText>
              Are you sure you want to delete voucher {deleteItemNo}? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setDeleteModalOpen(false)} variant="outlined">Cancel</Button>
            <Button onClick={handleDeleteConfirm} variant="contained" color="error">Delete</Button>
          </DialogActions>
        </Dialog>

        {/* Activate Confirmation Dialog */}
        <Dialog open={activateModalOpen} onClose={() => setActivateModalOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CheckCircle />
              Confirm Activation
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <DialogContentText>
              Are you sure you want to activate voucher {activateItemNo}?
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setActivateModalOpen(false)} variant="outlined">Cancel</Button>
            <Button onClick={handleActivateConfirm} variant="contained" color="success">Activate</Button>
          </DialogActions>
        </Dialog>

        {/* Journal Entry Modal */}
        <JournalEntryModal
          open={openModal}
          onClose={handleCloseModal}
          onSave={handleSaveJournalEntry}
          modalAction={modalAction}
          formData={formData}
          setFormData={setFormData}
          journalEntryDetails={journalEntryDetails}
          setJournalEntryDetails={setJournalEntryDetails}
          masters={masters}
          gstRates={gstRates}
          loading={modalLoading}
          companyId={companyId}
          token={token}
          branchId={branchId}
          fromDate={fromDate}
          toDate={toDate}
          fetchMaxBillNo={fetchMaxBillNo}
        />

        {/* Success/Error Snackbar */}
        <Snackbar
          open={!!success}
          autoHideDuration={3000}
          onClose={() => setSuccess('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={() => setSuccess('')} severity="success" sx={{ width: '100%' }}>
            {success}
          </Alert>
        </Snackbar>

        <Snackbar
          open={!!error}
          autoHideDuration={3000}
          onClose={() => setError('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>
      </div>
    </Fade>
  );
};

export default JournalEntryIndex;
